import { useEffect, useRef, useState } from "react";
import { isGalleryContentType, isHtmlContentType } from "../../../shared/gallery-content";
import { MAX_INSCRIPTION_FILE_BYTES } from "../../lib/batch";

export const GALLERY_FILE_ACCEPT = "image/*,text/html,.html,.htm";

export interface GalleryFile {
  file: File;
  mimeType: string;
  body: Uint8Array;
}

export function galleryFileMimeType(file: Pick<File, "name" | "type">): string {
  if (isHtmlContentType(file.type)) return file.type.trim();
  // Some browsers leave HTML's MIME empty or report it as plain text.
  if (/\.html?$/i.test(file.name)) return "text/html";
  return file.type;
}

export async function readGalleryFile(file: File): Promise<GalleryFile> {
  const mimeType = galleryFileMimeType(file);
  if (!isGalleryContentType(mimeType)) throw new Error("Choose an image or HTML file for the gallery cover.");
  if (file.size > MAX_INSCRIPTION_FILE_BYTES) throw new Error("Gallery files must be 400 KB or smaller.");
  const body = new Uint8Array(await file.arrayBuffer());
  if (!body.length) throw new Error("The gallery cover file is empty.");
  if (body.length > MAX_INSCRIPTION_FILE_BYTES) throw new Error("Gallery files must be 400 KB or smaller.");
  return { file, mimeType, body };
}

export function useGalleryFile() {
  const [selection, setSelection] = useState<GalleryFile | null>(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const revision = useRef(0);
  useEffect(() => () => { revision.current += 1; }, []);

  const clear = () => {
    revision.current += 1;
    setSelection(null);
    setError("");
    setPending(false);
  };

  const chooseFiles = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    clear();
    const current = revision.current;
    setPending(true);
    void readGalleryFile(file)
      .then((next) => { if (current === revision.current) setSelection(next); })
      .catch((caught) => {
        if (current === revision.current) setError(caught instanceof Error ? caught.message : "That cover file could not be read.");
      })
      .finally(() => { if (current === revision.current) setPending(false); });
  };

  return { selection, error, pending, chooseFiles, clear };
}
